import { action, computed, set } from 'mobx';
import { IAccessRequestResponse } from '~/types/server.types';
import {
    daysDifference,
    getFutureDate,
    isFutureDate
} from '~/utils/date.utils';

const DAYS_UNTIL_EXPIRES_SOON = 14;

export enum AccessRequestStatuses {
    PENDING = 'pending',
    REVOKED = 'revoked',
    ARCHIVED = 'archived',
    APPROVED = 'approved',
    PENDING_EXTENSION = 'pending_extension'
}

export enum AccessRequestFilterType {
    ALL = 'all',
    PENDING = 'pending',
    APPROVED = 'approved',
    REVOKED = 'revoked',
    ARCHIVED = 'archived'
}

enum ServerOnlyStatuses {
    EXPIRED = 'expired'
}

export type TInferredStatuses = Exclude<
    AccessRequestStatuses | ServerOnlyStatuses,
    AccessRequestStatuses.PENDING_EXTENSION
>;

type TUnauthorizedStatuses =
    | AccessRequestStatuses.PENDING
    | AccessRequestStatuses.REVOKED
    | AccessRequestStatuses.ARCHIVED;

export enum AccountRequestActions {
    APPROVE = 'approve',
    REVOKE = 'revoke',
    ARCHIVE = 'archive',
    EXTEND = 'extend'
}

export type TBookkeeperAccessActions = Extract<
    AccountRequestActions,
    AccountRequestActions.EXTEND | AccountRequestActions.REVOKE
>;

export abstract class AccountAccessAuthorization {
    _id: string;
    expires_at: Date | null;
    status: AccessRequestStatuses;

    constructor(request: IAccessRequestResponse) {
        set(this, request);
    }

    @computed
    get requestStatus(): TInferredStatuses {
        if (this.isUnauthorized(this.status)) {
            return this.status;
        }

        return this.isExpired
            ? ServerOnlyStatuses.EXPIRED
            : AccessRequestStatuses.APPROVED;
    }

    @computed
    get isExpired() {
        return !!this.expires_at && !isFutureDate(this.expires_at);
    }

    @computed
    get isExpiredOrAboutTo() {
        const doesExpireSoon =
            !!this.expires_at &&
            daysDifference(this.expires_at, new Date()) <=
                DAYS_UNTIL_EXPIRES_SOON;

        return this.isExpired || doesExpireSoon;
    }

    @computed
    get isAccessAuthorized() {
        return !this.isExpired && !this.hasUnauthorizedStatus;
    }

    @computed
    get hasUnauthorizedStatus() {
        return this.isUnauthorized(this.status);
    }

    isUnauthorized(
        requestStatus: AccessRequestStatuses
    ): requestStatus is TUnauthorizedStatuses {
        const unauthorizedStatuses = [
            AccessRequestStatuses.PENDING,
            AccessRequestStatuses.REVOKED,
            AccessRequestStatuses.ARCHIVED
        ];

        return unauthorizedStatuses.includes(requestStatus);
    }

    @action
    changeStatus(newStatus: AccessRequestStatuses) {
        this.status = newStatus;
    }

    get expirationDays() {
        return 0;
    }

    @action
    approveLocally() {
        this.changeStatus(AccessRequestStatuses.APPROVED);
        const now = new Date();
        this.expires_at = new Date(
            getFutureDate(now, 'day', this.expirationDays)
        );
    }
}
